/**
 * Cache persistant localStorage pour l'app native — jamais utilisé sur le web.
 * Clés scopées par userId, TTL configurable, versionnées.
 */

import { Capacitor } from '@capacitor/core';
import { safeLocalStorageGetItem } from '../utils/safeStorage';

const CACHE_PREFIX = 'native_cache_v1';

interface CacheEnvelope<T> {
  version: number;
  userId: number;
  timestamp: number;
  data: T;
}

export function isNativeCacheEnabled(): boolean {
  return Capacitor.isNativePlatform();
}

function cacheKey(suffix: string, userId: number): string {
  return `${CACHE_PREFIX}:${suffix}:${userId}`;
}

function safeSetItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* quota / storage interdit */
  }
}

function safeRemoveItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

export function readNativeCache<T>(
  suffix: string,
  userId: number,
  opts: { version?: number; ttlMs?: number } = {},
): T | null {
  if (!isNativeCacheEnabled()) return null;

  const version = opts.version ?? 1;
  const ttlMs = opts.ttlMs ?? 24 * 60 * 60 * 1000; // 24h par défaut

  const raw = safeLocalStorageGetItem(cacheKey(suffix, userId));
  if (!raw) return null;

  try {
    const envelope = JSON.parse(raw) as CacheEnvelope<T>;
    if (envelope.version !== version || envelope.userId !== userId) {
      safeRemoveItem(cacheKey(suffix, userId));
      return null;
    }
    if (Date.now() - envelope.timestamp > ttlMs) {
      safeRemoveItem(cacheKey(suffix, userId));
      return null;
    }
    return envelope.data;
  } catch {
    safeRemoveItem(cacheKey(suffix, userId));
    return null;
  }
}

export function writeNativeCache<T>(
  suffix: string,
  userId: number,
  data: T,
  version = 1,
): void {
  if (!isNativeCacheEnabled()) return;

  const envelope: CacheEnvelope<T> = {
    version,
    userId,
    timestamp: Date.now(),
    data,
  };
  safeSetItem(cacheKey(suffix, userId), JSON.stringify(envelope));
}

export function invalidateNativeCache(suffix: string, userId: number): void {
  if (!isNativeCacheEnabled()) return;
  safeRemoveItem(cacheKey(suffix, userId));
}

/** Clés de cache standardisées */
export const NativeCacheKeys = {
  PROFILE: 'profile',
  UPCOMING_BOOKINGS: 'upcoming_bookings',
} as const;

/** TTL : profil 24h, RDV 30 min (données plus volatiles) */
export const NativeCacheTtl = {
  PROFILE_MS: 24 * 60 * 60 * 1000,
  BOOKINGS_MS: 30 * 60 * 1000,
} as const;
